"use client";

import { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import type { GameId } from "modules/common/model/models";
import { getGameMetadata } from "modules/common/gameRegistry";
import { GameIcon } from "modules/common/components/Icons/GameIcon";
import type { FacetBucket } from "modules/collections/details/model/collection";

interface FacetGamePillsProps {
  buckets: FacetBucket[];
  selected: string[];
  onToggle: (game: string) => void;
  defaultOpen?: boolean;
  onClear?: () => void;
}

export function FacetGamePills({
  buckets,
  selected,
  onToggle,
  defaultOpen = true,
  onClear,
}: FacetGamePillsProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const visibleGames = useMemo(
    () =>
      buckets
        .filter((bucket) => bucket.count > 0)
        .map((bucket) => ({
          game: String(bucket.value) as GameId,
          count: bucket.count,
        })),
    [buckets]
  );
  const activeCount = selected.length;

  if (visibleGames.length <= 1) return null;

  return (
    <div className="border-base-content/10 border-b last:border-b-0">
      <div className="hover:bg-base-300 flex items-center transition-colors">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 px-2 py-1.5 text-sm font-medium"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`}
          />
          <span className="flex-1 text-left">Games</span>
          {activeCount > 0 && (
            <span className="badge badge-primary badge-xs">{activeCount}</span>
          )}
        </button>
        {activeCount > 0 && onClear && (
          <button
            type="button"
            className="text-base-content/50 hover:text-error pr-2 text-xs transition-colors"
            onClick={onClear}
          >
            Clear
          </button>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-wrap gap-1.5 px-2 pb-2">
            {visibleGames.map(({ game, count }) => {
              const isSelected = selected.includes(game);
              const metadata = getGameMetadata(game);

              return (
                <button
                  key={game}
                  type="button"
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    isSelected
                      ? "border-primary/50 bg-primary/5 text-primary"
                      : "border-base-content/15 hover:border-base-content/30 text-base-content/70"
                  }`}
                  onClick={() => onToggle(game)}
                >
                  <GameIcon game={game} size={16} />
                  <span>{metadata.name}</span>
                  <span
                    className={`tabular-nums ${isSelected ? "text-primary/60" : "text-base-content/40"}`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
